import { useMemo } from 'react';
import { useTexture } from '@react-three/drei';
import * as THREE from 'three';
import { BOARD_Z0, BOARD_SPACING } from '../../data/skillBoards';
import { TUNNEL_EXIT_Z } from '../../data/tunnel';
import { useTunnelExitStore } from './tunnelExitStore';
import { createExitManifestoTexture } from './tunnelExitStadiumTextures';

function isExitCinematic(beat: string) {
  return beat === 'exit' || beat === 'fade';
}

/** Fifth skill card (Three.js) — stadium starts to bloom as you arrive. */
const CARD5_Z = BOARD_Z0 + BOARD_SPACING * 4;

export function stadiumRevealFromZ(runnerZ: number, cinematic: boolean) {
  if (cinematic) return 1;
  return THREE.MathUtils.smoothstep(runnerZ, CARD5_Z - 2.4, CARD5_Z + 11);
}

const GOLD = '#f0c84a';
const CONCRETE = '#1c1f26';
const CONCRETE_EDGE = '#14171c';

function ExitRailing({ side, z0 }: { side: 'left' | 'right'; z0: number }) {
  const sign = side === 'left' ? -1 : 1;
  const railMat = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#c5ccd6',
        metalness: 0.92,
        roughness: 0.18,
      }),
    [],
  );

  const length = 8.2;
  const postCount = 7;
  const postSpacing = length / (postCount - 1);
  const x = sign * 2.22;

  return (
    <group position={[x, 0, z0 - 4.1]}>
      <mesh position={[0, 0.92, 0]} rotation={[Math.PI / 2, 0, 0]} material={railMat}>
        <cylinderGeometry args={[0.026, 0.026, length, 12]} />
      </mesh>
      <mesh position={[0, 0.5, 0]} rotation={[Math.PI / 2, 0, 0]} material={railMat}>
        <cylinderGeometry args={[0.016, 0.016, length, 10]} />
      </mesh>
      {Array.from({ length: postCount }, (_, i) => {
        const pz = -length / 2 + i * postSpacing;
        return (
          <mesh key={i} position={[0, 0.46, pz]} material={railMat}>
            <cylinderGeometry args={[0.018, 0.018, 0.92, 10]} />
          </mesh>
        );
      })}
    </group>
  );
}

function GoldKickLights({ z0 }: { z0: number }) {
  const zs = [-7.2, -5.6, -4.0, -2.4, -0.8];
  return (
    <group>
      {zs.map((dz) =>
        ([-2.32, 2.32] as const).map((x) => (
          <group key={`${x}-${dz}`} position={[x, 0.03, z0 + dz]}>
            <mesh>
              <boxGeometry args={[0.34, 0.05, 0.12]} />
              <meshStandardMaterial color="#1a1c20" roughness={0.5} metalness={0.6} />
            </mesh>
            <mesh position={[0, 0.03, 0]}>
              <boxGeometry args={[0.26, 0.03, 0.08]} />
              <meshBasicMaterial color="#ffc94a" toneMapped={false} />
            </mesh>
            <pointLight
              position={[x < 0 ? 0.16 : -0.16, 0.2, 0]}
              intensity={0.58}
              distance={2.6}
              decay={2}
              color="#ffb830"
            />
          </group>
        )),
      )}
    </group>
  );
}

function LedFrame({ z0, reveal }: { z0: number; reveal: number }) {
  const w = 5.15;
  const h = 3.92;
  const t = 0.055;
  const d = 0.08;
  const y = 2.02;
  const mat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: GOLD,
        toneMapped: false,
        fog: false,
        transparent: true,
        opacity: 0,
      }),
    [],
  );
  mat.opacity = 0.12 + reveal * 0.88;
  return (
    <group position={[0, y, z0 + 0.02]}>
      <mesh position={[0, h / 2, 0]} material={mat}>
        <boxGeometry args={[w + t, t, d]} />
      </mesh>
      <mesh position={[0, -h / 2, 0]} material={mat}>
        <boxGeometry args={[w + t, t, d]} />
      </mesh>
      <mesh position={[-w / 2, 0, 0]} material={mat}>
        <boxGeometry args={[t, h, d]} />
      </mesh>
      <mesh position={[w / 2, 0, 0]} material={mat}>
        <boxGeometry args={[t, h, d]} />
      </mesh>
    </group>
  );
}

function MouthWalls({ z0 }: { z0: number }) {
  const wall = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: CONCRETE,
        roughness: 0.9,
        metalness: 0.06,
      }),
    [],
  );
  const edge = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: CONCRETE_EDGE,
        roughness: 0.88,
        metalness: 0.08,
      }),
    [],
  );
  const manifesto = useMemo(() => createExitManifestoTexture(), []);
  const badge = useTexture('/images/ad-image.png');
  badge.colorSpace = THREE.SRGBColorSpace;
  badge.anisotropy = 8;

  return (
    <group>
      <mesh position={[-2.58, 2.02, z0 - 4.1]} material={wall}>
        <boxGeometry args={[0.42, 4.05, 8.4]} />
      </mesh>
      <mesh position={[2.58, 2.02, z0 - 4.1]} material={wall}>
        <boxGeometry args={[0.42, 4.05, 8.4]} />
      </mesh>
      <mesh position={[0, 4.12, z0 - 4.1]} material={wall}>
        <boxGeometry args={[5.6, 0.22, 8.4]} />
      </mesh>
      {([-2.28, 2.28] as const).map((x) => (
        <mesh key={`ceil-led-${x}`} position={[x, 3.98, z0 - 4.1]}>
          <boxGeometry args={[0.045, 0.03, 8.3]} />
          <meshBasicMaterial color={GOLD} toneMapped={false} />
        </mesh>
      ))}

      <mesh position={[-2.82, 2.02, z0 + 0.12]} material={edge}>
        <boxGeometry args={[0.55, 4.12, 0.28]} />
      </mesh>
      <mesh position={[2.82, 2.02, z0 + 0.12]} material={edge}>
        <boxGeometry args={[0.55, 4.12, 0.28]} />
      </mesh>
      <mesh position={[0, 4.18, z0 + 0.12]} material={edge}>
        <boxGeometry args={[6.2, 0.28, 0.28]} />
      </mesh>
      <mesh position={[0, -0.02, z0 + 0.12]} material={edge}>
        <boxGeometry args={[6.2, 0.12, 0.28]} />
      </mesh>

      <mesh
        position={[-2.36, 2.15, z0 - 3.15]}
        rotation={[0, Math.PI / 2, 0]}
      >
        <planeGeometry args={[5.6, 3.5]} />
        <meshBasicMaterial
          map={manifesto}
          transparent
          depthWrite={false}
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh
        position={[2.36, 2.85, z0 - 3.35]}
        rotation={[0, -Math.PI / 2, 0]}
      >
        <planeGeometry args={[1.55, 1.55]} />
        <meshBasicMaterial
          map={badge}
          transparent
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

function StadiumPlate({ z0, reveal }: { z0: number; reveal: number }) {
  const stadiumTex = useTexture('/images/tunnel-exit-stadium-blur.png');
  stadiumTex.colorSpace = THREE.SRGBColorSpace;
  stadiumTex.minFilter = THREE.LinearFilter;
  stadiumTex.magFilter = THREE.LinearFilter;
  stadiumTex.generateMipmaps = false;
  stadiumTex.anisotropy = 8;

  return (
    <group>
      <mesh position={[0, 2.02, z0 + 1.15]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[8.4, 4.35]} />
        <meshBasicMaterial
          map={stadiumTex}
          fog
          transparent
          opacity={reveal}
          side={THREE.DoubleSide}
          toneMapped={false}
          depthWrite={reveal > 0.85}
        />
      </mesh>
      {/* Dark shutter — fully covers the mouth until the fifth card */}
      <mesh position={[0, 2.02, z0 + 0.35]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[5.4, 4.15]} />
        <meshBasicMaterial
          color="#050608"
          transparent
          opacity={1 - reveal}
          depthWrite={reveal < 0.5}
          fog={false}
        />
      </mesh>
    </group>
  );
}

export function TunnelExitPortal({ reveal }: { reveal: number }) {
  const exitBeat = useTunnelExitStore((s) => s.beat);
  const exitCinematic = isExitCinematic(exitBeat);
  const z0 = TUNNEL_EXIT_Z;
  const mouthLit = exitCinematic ? 1 : reveal;

  return (
    <group>
      <pointLight
        position={[0, 3.1, z0 + 4]}
        intensity={2.2 * mouthLit}
        color="#ffe7b0"
        distance={22}
        decay={2}
      />
      <pointLight
        position={[0, 2.4, z0 - 2]}
        intensity={1.1 * mouthLit}
        color="#f0c84a"
        distance={12}
        decay={2}
      />

      <MouthWalls z0={z0} />
      <LedFrame z0={z0} reveal={reveal} />
      <ExitRailing side="left" z0={z0} />
      <ExitRailing side="right" z0={z0} />
      <GoldKickLights z0={z0} />
      <StadiumPlate z0={z0} reveal={reveal} />
    </group>
  );
}
